import { useEffect, useMemo, useRef, useState } from "react";
import Controlls from "./Controlls";
import UserNameAndTime from "./UserNameAndTime";
import { useEffectiveMainStream } from "../../../hooks/useEffectiveMainStream";
import { useStreamAvatar } from "../../../hooks/useStreamAvatar";
import { useStream } from "../../../context/StreamProvider";
import { useIsGroupCall } from "../../../hooks/useIsGroupCall";
import { useAppSelector } from "../../../utils/hooks";
import {
  startOutgoingWaitSound,
  stopOutgoingWaitSound,
} from "../../../utils/callSounds";
import { getCallPeerSnapshot } from "../../../utils/callPeerSnapshot";

type MainVideoProps = {
  /** true: рядом есть полоса превью (группа); false: основное видео на всю высоту */
  compactTopStrip?: boolean;
};

function getInitials(name: string) {
  return (
    (name || "?")
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((p) => p.charAt(0).toUpperCase())
      .join("") || "?"
  );
}

export default function MainVideo({ compactTopStrip = true }: MainVideoProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const [imgBroken, setImgBroken] = useState(false);
  const [connectingImgBroken, setConnectingImgBroken] = useState(false);
  const [narrowPortrait, setNarrowPortrait] = useState(false);

  const { userStreams } = useStream();
  const isGroupCall = useIsGroupCall();
  const isRemoteMuted = useAppSelector(
    (s) => s.PeerJsSlice.isRemoteMuted,
  );

  const currentStream = useEffectiveMainStream();
  const { url: profilePic, initials } = useStreamAvatar(currentStream);
  const callPeer = getCallPeerSnapshot();

  const hasRemote = useMemo(
    () => Object.values(userStreams).some((s) => !s.isLocal),
    [userStreams],
  );
  const isPrivateConnecting = !isGroupCall && !hasRemote;
  const isCaller =
    typeof window !== "undefined" &&
    sessionStorage.getItem("is_caller") === "true";
  /** Outgoing wait tone: caller waiting for accept / first remote peer. */
  const playOutgoingWait = isCaller && !hasRemote;

  // Always from call snapshot — CurrentConversation changes when browsing chats
  const connectingName = callPeer.title || "";
  const connectingPic = callPeer.avatar || null;
  const connectingInitials = getInitials(connectingName);
  const hasConnectingPic = !!connectingPic && !connectingImgBroken;

  useEffect(() => {
    if (playOutgoingWait) startOutgoingWaitSound();
    else stopOutgoingWaitSound();
    return () => stopOutgoingWaitSound();
  }, [playOutgoingWait]);

  useEffect(() => {
    if (isPrivateConnecting) return;
    if (videoRef.current && currentStream?.stream) {
      videoRef.current.srcObject = currentStream.stream;
    }
    if (audioRef.current && currentStream?.stream) {
      audioRef.current.srcObject = currentStream.stream;
    }
    setImgBroken(false);
  }, [currentStream, isPrivateConnecting]);

  useEffect(() => {
    setConnectingImgBroken(false);
  }, [connectingPic]);

  useEffect(() => {
    const mq = window.matchMedia(
      "(max-width: 639px) and (orientation: portrait)",
    );
    const apply = () => setNarrowPortrait(mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  const showVideo = !isPrivateConnecting && !!currentStream?.isVideoEnabled;
  const useVerticalVideoFrame = narrowPortrait && showVideo;
  const hasValidPic = !!profilePic && !imgBroken;

  return (
    <div
      className={`relative min-h-0 overflow-hidden rounded-3xl bg-gradient-to-br from-zinc-900 via-zinc-950 to-black shadow-2xl ring-1 ring-white/10 ${
        compactTopStrip ? "h-[65%] shrink-0" : "flex-1"
      }`}
    >
      {isPrivateConnecting ? (
        <div className="relative h-full w-full">
          {hasConnectingPic && (
            <div
              className="absolute inset-0 scale-110 bg-cover bg-center blur-2xl"
              style={{ backgroundImage: `url(${connectingPic})` }}
              aria-hidden="true"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/70" />

          <div className="relative grid h-full w-full place-content-center">
            <div className="flex flex-col items-center gap-6 px-6 text-center">
              <div className="relative">
                <div className="absolute -inset-4 animate-pulse rounded-full bg-white/10 blur-xl" />
                <div className="absolute -inset-6 animate-ping rounded-full bg-white/5" />
                {hasConnectingPic ? (
                  <img
                    src={connectingPic!}
                    onError={() => setConnectingImgBroken(true)}
                    className="relative h-36 w-36 rounded-full object-cover ring-4 ring-white/20 shadow-2xl sm:h-44 sm:w-44 lg:h-52 lg:w-52 2xl:h-64 2xl:w-64"
                    alt=""
                  />
                ) : (
                  <div className="relative grid h-36 w-36 place-content-center rounded-full bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 text-4xl font-semibold text-white ring-4 ring-white/20 shadow-2xl sm:h-44 sm:w-44 sm:text-5xl lg:h-52 lg:w-52 lg:text-6xl 2xl:h-64 2xl:w-64 2xl:text-7xl">
                    {connectingInitials}
                  </div>
                )}
              </div>

              {connectingName && (
                <div className="text-2xl font-semibold text-white drop-shadow-lg sm:text-3xl lg:text-4xl">
                  {connectingName}
                </div>
              )}

              <div className="flex items-center gap-2 rounded-full border border-white/10 bg-black/40 px-4 py-2 text-sm font-medium text-white/90 backdrop-blur-xl">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
                </span>
                <span>Connecting</span>
                <span className="inline-flex">
                  <span className="animate-bounce [animation-delay:-0.3s]">.</span>
                  <span className="animate-bounce [animation-delay:-0.15s]">.</span>
                  <span className="animate-bounce">.</span>
                </span>
              </div>
            </div>
          </div>
        </div>
      ) : showVideo ? (
        <div
          className={
            useVerticalVideoFrame
              ? "flex h-full w-full items-center justify-center bg-black"
              : "h-full w-full"
          }
        >
          <div
            className={
              useVerticalVideoFrame
                ? "flex h-full max-h-full w-full max-w-full justify-center [aspect-ratio:9/16]"
                : "h-full w-full"
            }
          >
            <video
              style={
                currentStream?.isLocal ? { transform: "scaleX(-1)" } : undefined
              }
              className="h-full w-full bg-black object-cover"
              ref={videoRef}
              autoPlay
              playsInline
              muted={!!currentStream?.isLocal || isRemoteMuted}
            />
          </div>
        </div>
      ) : (
        <div className="relative h-full w-full">
          {hasValidPic && (
            <div
              className="absolute inset-0 scale-110 bg-cover bg-center blur-2xl"
              style={{ backgroundImage: `url(${profilePic})` }}
              aria-hidden="true"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/70" />

          <div className="relative grid h-full w-full place-content-center">
            <div className="flex flex-col items-center gap-4">
              <div className="relative">
                <div className="absolute -inset-3 animate-pulse rounded-full bg-white/10 blur-xl" />
                {hasValidPic ? (
                  <img
                    src={profilePic!}
                    onError={() => setImgBroken(true)}
                    className="relative h-36 w-36 rounded-full object-cover ring-4 ring-white/20 shadow-2xl sm:h-44 sm:w-44 lg:h-52 lg:w-52 2xl:h-64 2xl:w-64"
                    alt=""
                  />
                ) : (
                  <div className="relative grid h-36 w-36 place-content-center rounded-full bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 text-4xl font-semibold text-white ring-4 ring-white/20 shadow-2xl sm:h-44 sm:w-44 sm:text-5xl lg:h-52 lg:w-52 lg:text-6xl 2xl:h-64 2xl:w-64 2xl:text-7xl">
                    {initials}
                  </div>
                )}
              </div>

              {currentStream?.isAudioEnabled && (
                <audio
                  muted={!!currentStream?.isLocal || isRemoteMuted}
                  autoPlay
                  className="hidden"
                  playsInline
                  ref={audioRef}
                />
              )}
            </div>
          </div>
        </div>
      )}

      {!isPrivateConnecting && <UserNameAndTime />}
      <Controlls />
    </div>
  );
}
